import React, { useState, useEffect } from "react";
import { Formik, ErrorMessage } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Breadcrums from "../../../common/breadcrumbs";
import CustomCkeditor from "../../../common/customeditor";
import { globalLoader, handleServerValidations } from "../../../../../utils/commonfunction";
import * as announcementServices from "../../../services/announcement.services"; 
import * as emailServices from "../../../services/email.template"; 
import { SWAL_SETTINGS } from "../../../../../utils/Constants";

const AdminEmailAnnouncement = () => {
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [templates, setTemplates] = useState([]);

  const breadcrumbs = [
    { title: "Dashboard", url: "/admin/dashboard" },
    { title: "Send Announcement", url: "" },
  ];

  useEffect(() => {
    // Fetch available email templates
    emailServices.List()
      .then((res) => {
        if (res.success && res.data?.list) {
          setTemplates(res?.data?.list);
        }
      })
      .catch(console.log);
  }, []);

  const validationSchema = Yup.object().shape({
      recipient_type: Yup.string().required("Please select recipient type"),

      // THIS LINE IS MANDATORY — fixes the "branch is not a function" error
      use_template: Yup.boolean(),

      template_id: Yup.string().when("use_template", {
          is: true,
          then: (schema) => schema.required("Please select a template"),
          otherwise: (schema) => schema.optional().nullable(),
      }),

      subject: Yup.string().when("use_template", {
          is: false,
          then: (schema) => schema.required("Subject is required").test("trim", "Subject is required", (value) => value?.trim().length > 0),
          otherwise: (schema) => schema.optional(),
      }),

      // body: Yup.string().when("use_template", {
      //   is: false,
      //   then: (schema) =>
      //     schema
      //       .required("Message body is required")
      //       .test("not-empty-html", "Message body cannot be empty", (value) => {
      //         if (!value) return false;
      //         // Remove HTML tags and check if there's actual text
      //         const text = value.replace(/<[^>]*>/g, "").trim();
      //         return text.length > 0;
      //       }),
      //   otherwise: (schema) => schema.optional(),
      // }),

      custom_emails: Yup.string().when("recipient_type", {
          is: "custom",
          then: (schema) => schema.required("Please enter at least one email address"),
          otherwise: (schema) => schema.optional(),
      }),
  });

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      <Formik
        initialValues={{
          recipient_type: "users",
          use_template: false,
          template_id: "",
          subject: "",
          body: "",
          custom_emails: "",
        }}
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting }) => {
          Swal.fire({
            title: "Send Announcement?",
            text: `This will send emails to all selected recipients`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, Send Now"
          }).then((result) => {
            console.log("Swal result:", result);
            if (!result.isConfirmed) return;

            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("recipient_type", values.recipient_type);
            formData.append("use_template", values.use_template);
            if (values.use_template && values.template_id) {
              formData.append("template_id", values.template_id);
            } else {
              formData.append("subject", values.subject);
              formData.append("body", values.body);
            }
            if (values.recipient_type === "custom") {
              formData.append("custom_emails", values.custom_emails);
            }
            console.log("Form Data to be sent:", values);

            announcementServices.sendEmailAnnouncement(formData)
              .then((response) => {
                setSubmitting(false);
                globalLoader(false);
                if (response.success) {
                  Swal.fire({
                    icon: "success",
                    text: response.message || "Announcement sent successfully!",
                    ...SWAL_SETTINGS,
                  });
                  setTimeout(() => navigate("/admin/dashboard"), 2000);
                } else {
                  Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                  });
                  setSubmitted(false);
                }
              })
              .catch((error) => {
                globalLoader(false);
                setSubmitting(false);
                setSubmitted(false);
                Swal.fire({
                  icon: "error",
                  text: "Something went wrong!",
                  ...SWAL_SETTINGS,
                });
              });
          });
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <div className="row row-sm">
              <div className="col-lg-12 col-md-12 animation_fade">
                <div className="card custom-card">
                  <div className="card-body">
                    <h6 className="main-content-label mb-4">Send Announcement / Notification</h6>

                    <div className="row row-sm">
                      {/* Recipient Type */}
                      <div className="col-md-6 form-group">
                        <label className="text-left d-flex">
                          Send To:<span className="requirestar">*</span>
                        </label>
                        <select
                          name="recipient_type"
                          className="form-control"
                          value={values.recipient_type}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        >
                          <option value="users">All Users</option>
                          <option value="astrologers">All Astrologers</option>
                          <option value="custom">Custom Email List</option>
                        </select>
                        <span className="text-danger d-flex text-left">
                          <ErrorMessage name="recipient_type" />
                        </span>
                      </div>

                      {/* Custom Emails (only if custom selected) */}
                      {values.recipient_type === "custom" && (
                        <div className="col-md-12 form-group">
                          <label className="text-left d-flex">
                            Emails (one per line):<span className="requirestar">*</span>
                          </label>
                          <textarea
                            name="custom_emails"
                            className="form-control"
                            rows="6"
                            placeholder="user1@example.com&#10;user2@example.com"
                            value={values.custom_emails}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          <span className="text-danger d-flex text-left">
                            <ErrorMessage name="custom_emails" />
                          </span>
                        </div>
                      )}

                      {/* Use Template Toggle */}
                      <div className="col-md-6 form-group">
                        <label className="text-left d-flex">Email Content Source:</label>
                        <div className="d-flex align-items-center">
                          <label className="me-4">
                            <input
                              type="radio"
                              name="use_template"
                              checked={!values.use_template}
                              onChange={() => setFieldValue("use_template", false)}
                            />{" "}
                            Write Custom Message
                          </label>
                          <label>
                             <input
                              type="radio"
                              name="use_template"
                              checked={values.use_template}
                              onChange={() => setFieldValue("use_template", true)}
                            />{" "}
                            Use Existing Template
                          </label>
                        </div>
                      </div>

                      {/* Template Selection */}
                      {values.use_template && (
                        <div className="col-md-12 form-group">
                          <label className="text-left d-flex">
                            Select Template:<span className="requirestar">*</span>
                          </label>
                          <select
                            name="template_id"
                            className="form-control"
                            value={values.template_id}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          >
                            <option value="">-- Select Template --</option>
                            {templates?.map((temp) => (
                              <option key={temp._id} value={temp._id}>
                                {temp.title} ({temp.slug})
                              </option>
                            ))}
                          </select>
                          <span className="text-danger d-flex text-left">
                            <ErrorMessage name="template_id" />
                          </span>
                        </div>
                      )}

                      {/* Custom Subject & Body */}
                      {!values.use_template && (
                        <>
                          <div className="col-md-12 form-group">
                            <label className="text-left d-flex">
                              Subject:<span className="requirestar">*</span>
                            </label>
                            <input
                              name="subject"
                              type="text"
                              className="form-control"
                              value={values.subject}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            <span className="text-danger d-flex text-left">
                              <ErrorMessage name="subject" />
                            </span>
                          </div>

                          {/* <div className="col-lg-12 form-group">
                            <label className="text-left d-flex">
                              Message Body:<span className="requirestar">*</span>
                            </label>
                            <CustomCkeditor
                              fieldname="body"
                              setFieldValue={setFieldValue}
                              value={values.body}
                              setFieldTouched={setFieldTouched}
                            />
                            <span className="text-danger d-flex text-left">
                              <ErrorMessage name="body" />
                            </span>
                          </div> */}
                          <div className='col-lg-12 text-center form-group'>
                            <label htmlFor='subject' className='text-left d-flex'>Message Body:<span className="requirestar">*</span></label>
                            <CustomCkeditor
                                fieldname={"body"}
                                setFieldValue={setFieldValue}
                                value={values.body}
                                setFieldTouched={setFieldTouched}
                            />
                            <span className='text-danger d-flex text-left'>{errors.body && touched.body && errors.body}</span>
                        </div>
                        </>
                      )}

                      {/* Buttons */}
                      <div className="col-12 mt-4">
                        <button
                          className="btn btn-main-primary signbtn mr-2"
                          type="submit"
                          disabled={submitted || isSubmitting}
                        >
                          <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                          {submitted ? "Sending..." : "Send Announcement"}
                        </button>
                        <button
                          className="btn ripple btn-secondary"
                          type="button"
                          onClick={() => navigate(-1)}
                          disabled={submitted}
                        >
                          <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </>
  );
};

export default AdminEmailAnnouncement;